// Logowanie (artboardy 04a–04e).
//
// Karta 440 px: tytuł, dwa pola, „Nie pamiętasz hasła?" pod polem hasła, jedna akcja
// i jedno zdanie o rejestracji. „Zapamiętaj mnie" wyleciało (MAPA 37, martwe klucze):
// sesja i tak trzyma się tak długo, jak trzyma się ciasteczko, a pole pytało o decyzję,
// której nikt nie podejmował.
//
// Trzy odmowy, które rozróżnia backend (app/auth/routes.py:228-235), miały kiedyś JEDNO
// zdanie: „zły e-mail lub hasło, albo adres niepotwierdzony". W dwóch z trzech przypadków
// było ono kłamstwem o haśle i wysyłało człowieka do formularza resetu, który nie miał
// jak pomóc. Dziś adres niepotwierdzony ma własne zdanie i własne wyjście: ponowną wysyłkę
// linku — jedyną drogę z jedynego ślepego zaułka tego ekranu.

import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams, Link } from 'react-router'
import { useAuth } from '../auth/AuthContext'
import { api, ApiError } from '../lib/api'
import { useT } from '../lib/i18n'
import { denialCode, describeApiError } from '../ds/feedback/apiError'
import AuthShell from '../components/AuthShell'
import Icon from '../components/Icon'
import { Button, Field } from '../ds'

export default function Login() {
  const { login } = useAuth()
  const t = useT()
  const navigate = useNavigate()
  const location = useLocation() as { state?: { from?: { pathname: string } } }
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // The server's reason, when it gave one. Only `email_unverified` changes what this
  // screen OFFERS; the rest change only what it says.
  const [denial, setDenial] = useState<string | null>(null)
  const [resend, setResend] = useState<'idle' | 'busy' | 'sent' | 'failed'>('idle')
  // The same 60 s countdown the "check your inbox" screen has after registering.
  const [cooldown, setCooldown] = useState(0)
  useEffect(() => {
    if (cooldown <= 0) return
    const id = setTimeout(() => setCooldown((s) => s - 1), 1000)
    return () => clearTimeout(id)
  }, [cooldown])

  // Outcomes handed back by an email-link navigation (app/auth/routes.py ->
  // _spa_login). Without this the user clicks "confirm your address", the token
  // IS consumed server-side, and they land on a login form that says nothing
  // happened — so they click the now-dead link again and conclude it is broken.
  const [params] = useSearchParams()
  const banner = ([
    ['verified', 'ok'],
    ['password_reset', 'ok'],
    ['email_changed', 'ok'],
    ['verify_error', 'danger'],
    ['verify_expired', 'danger'],
    ['email_taken', 'danger'],
    ['email_change_error', 'danger'],
  ] as const).find(([key]) => params.get(key) === '1')
  const BANNER_KEY: Record<string, string> = {
    verified: 'auth.banner.verified',
    password_reset: 'auth.banner.passwordReset',
    email_changed: 'auth.banner.emailChanged',
    verify_error: 'auth.banner.verifyError',
    verify_expired: 'auth.banner.verifyExpired',
    email_taken: 'auth.banner.emailTaken',
    email_change_error: 'auth.banner.emailChangeError',
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null); setDenial(null); setResend('idle'); setCooldown(0); setBusy(true)
    try {
      await login(email, password, false)
      navigate(location.state?.from?.pathname ?? '/edits', { replace: true })
    } catch (err) {
      const code = denialCode(err)
      setDenial(code)
      // Only a credential refusal may say the password is wrong (api.ts: a 401
      // `invalid_credentials` is ApiError(401); a bare 401 is a session that ended). A 429
      // (10 tries per 15 min per IP — one carrier NAT spends that window for many people),
      // a 5xx or a dead network is not the person's mistake: it gets the common
      // "our side, try again" sentence instead of a push to retype (and re-lock) or to a
      // password reset nobody needs. Measured on prod 2026-09-25 (ui-ux-score loop).
      const wrongCredentials = (err instanceof ApiError && err.status === 401)
        || (err instanceof Error && err.message === 'unauthorized')
      setError(code || !wrongCredentials ? describeApiError(err, t).title : t('auth.login.error'))
    } finally {
      setBusy(false)
    }
  }

  /**
   * The way out of this screen's one dead end.
   *
   * `/auth/resend-verification` has been public since it was written (it has to be:
   * login 403s an unverified user, so they have no session), but nothing in the SPA
   * offered it — the person was told to confirm their address and given nowhere to ask
   * for the link again.
   */
  async function resendVerification() {
    setResend('busy')
    try {
      await api('/auth/resend-verification', { method: 'POST', body: JSON.stringify({ email }) })
      setResend('sent')
      setCooldown(60)
    } catch {
      // The endpoint answers 200 whether or not the address exists (anti-enumeration),
      // so a throw here is a transport failure or the 5-per-hour limiter — never
      // "no such account". Saying "sent" anyway would be the comfortable lie.
      setResend('failed')
    }
  }

  const unverified = denial === 'email_unverified'
  // Zły e-mail albo hasło to błąd POLA: 401 mówi o tym, co jest w polach. Każda inna
  // odmowa (konto zawieszone, konto czeka) mówi o koncie, więc stoi nad formularzem —
  // pod polem hasła byłaby zdaniem o czymś, czego to pole nie dotyczy.
  const fieldError = error && !denial ? error : undefined
  const accountError = error && denial && !unverified ? error : undefined

  return (
    <AuthShell>
      <h1>{t('auth.login.heading')}</h1>

      {/* Jedna linia potwierdzenia po powrocie z maila (artboard 04e). */}
      {banner && !error && (
        <p className={banner[1] === 'ok' ? 'mp-ok' : 'mp-err'} role="status">
          {banner[1] === 'ok' ? <Icon name="Check" size={16} strokeWidth={2.5} /> : null}
          {t(BANNER_KEY[banner[0]])}
        </p>
      )}

      {accountError && <p className="mp-err" role="alert">{accountError}</p>}

      <form onSubmit={onSubmit}>
        <Field
          id="email"
          type="email"
          label={t('auth.login.emailLabel')}
          placeholder={t('auth.login.emailPlaceholder')}
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        {/* Bez zastępczego tekstu: rząd kropek czyta się jak pole już wypełnione. */}
        <Field
          id="pass"
          type="password"
          label={t('auth.login.passwordLabel')}
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={fieldError}
          required
        />
        <div className="mp-auth-forgot">
          <Link to="/forgot" className="mn-lnk">{t('auth.login.forgot')}</Link>
        </div>

        {/* Odmowa, która MA następny krok: hasło było dobre, brakuje potwierdzenia
            adresu. Zdanie i droga stoją w jednym wierszu, pod polami, których dotyczą. */}
        {unverified && resend !== 'sent' && (
          <div className="mp-auth-row" data-testid="login-resend">
            <span className="mp-err" role="alert">{t('auth.login.unverified')}</span>
            <button type="button" className="mn-lnk" disabled={resend === 'busy' || cooldown > 0}
                    onClick={() => void resendVerification()}>
              {cooldown > 0 ? t('auth.register.check.resendWait', { s: cooldown }) : t('auth.login.resend')}
            </button>
          </div>
        )}
        {unverified && resend === 'sent' && (
          <p className="mp-ok" role="status">
            <Icon name="Check" size={16} strokeWidth={2.5} />{t('auth.login.resendSent')}
          </p>
        )}
        {unverified && resend === 'failed' && (
          <p className="mp-err" role="alert">{t('auth.login.resendFailed')}</p>
        )}
        <Button type="submit" variant="primary" busy={busy}>{t('auth.login.submit')}</Button>
      </form>

      <p className="mp-auth-foot is-centered">
        {t('auth.login.noAccount')}{' '}
        <Link to="/register" className="mn-lnk">{t('auth.login.signUp')}</Link>
      </p>
    </AuthShell>
  )
}
